import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Space,
  Input,
  Button,
  Typography,
  Modal,
  Radio,
  Divider,
  List,
  Popconfirm,
  Dropdown,
  message,
  Tooltip,
  Spin,
} from "antd";
import {
  Plus,
  MoreHorizontal,
  Upload,
  Trash2,
  Shield,
  Mail,
  Search as SearchIcon,
} from "lucide-react";
import { preventCopyProps } from "views/util/protected";
import { notificationErroInternalGeneric } from "util/api-utils";
import {
  deleteByType,
  getData,
  saveData,
} from "services/managerial/managerial-service";
import { useDebounce } from "views/util/debounce";

const { Text } = Typography;

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(String(v).trim());

const parseEmails = (raw) => {
  const tokens = String(raw)
    .split(/[\s,;]+/g)
    .map((t) => t.trim())
    .filter(Boolean);

  const found = tokens
    .filter((e) => e.trim() != "" && e.includes("@"))
    .map((e) => e.toLowerCase());

  return found;
};

export default function EmailCards({ protectedView = false }) {
  const [loading, setLoading] = useState(false);

  const [emails, setEmails] = useState({
    total: 0,
    items: [],
    size: 20,
    page: 0,
  });

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkMode, setBulkMode] = useState("REPLACE");
  const [query, setQuery] = useState("");

  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const listContainerRef = useRef(null);

  const menu = useMemo(
    () => ({
      items: [
        {
          key: "bulk",
          icon: <Upload size={14} />,
          label: "Colar lista de e-mails",
          onClick: () => setBulkOpen(true),
        },
        { type: "divider" },
        {
          key: "clear",
          icon: <Trash2 size={14} />,
          label: "Limpar todos",
          danger: true,
          onClick: () => removeAll(),
        },
      ],
    }),
    []
  );

  const removeOne = async (id) => {
    const url = "EMAIL/" + id;
    await deleteByType(url);
    findData();
  };
  const handleBulkSave = async () => {
    const list = parseEmails(bulkText);
    if (!list.length) {
      message.warning?.("Nenhum e-mail válido encontrado");
      return;
    }
    setLoading(true);

    try {
      const obj = {
        values: list,
        type: bulkMode,
        value: "EMAIL",
      };

      await saveData(obj);
      setBulkText("");
      setBulkOpen(false);
      message.success?.(
        bulkMode === "REPLACE" ? "Lista substituída" : "Telefones adicionados"
      );
      findData();
    } catch (error) {
      notificationErroInternalGeneric();
    } finally {
      setLoading(false);
    }
  };

  const removeAll = async () => {
    const obj = {
      values: [],
      type: "REPLACE",
      value: "EMAIL",
    };

    await saveData(obj);
    findData();
  };

  useEffect(() => {
    findData();
  }, []);

  const findData = async (append = false) => {
    if (!hasMore && append) return;

    setLoading(true);

    try {
      const filter = {
        search: query,
        page: page,
        size: size,
      };

      const result = await getData("EMAIL", filter);
      const newItems = result.items || [];

      setEmails((prev) => ({
        ...prev,
        total: result.total,
        items: append ? [...prev.items, ...newItems] : newItems,
      }));
      setHasMore(newItems.length === size);
      if (append) setPage((prev) => prev + 1);
      else setPage(0);
    } catch {
      notificationErroInternalGeneric();
    } finally {
      setLoading(false);
    }
  };

  const debouncedFindData = useDebounce(findData, 800);

  useEffect(() => {
    debouncedFindData();
  }, [query]);

  useEffect(() => {
    return () => {
      debouncedFindData.cancel();
    };
  }, [debouncedFindData]);

  const handleLoadMore = () => {
    const container = listContainerRef.current;
    const scrollTopBefore = container.scrollTop;

    findData(true).then(() => {
      setTimeout(() => {
        container.scrollTop = scrollTopBefore;
      }, 0);
    });
  };

  return (
    <Spin spinning={loading}>
      <Card
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Space>
              <Mail size={18} style={{ marginTop: 4 }} />
              <Text>Email ({emails.total})</Text>

              {protectedView && (
                <Tooltip title="Protegido">
                  <Shield size={14} style={{ marginTop: 2 }} />
                </Tooltip>
              )}
            </Space>

            <div
              style={{
                fontSize: 12,
                color: "rgba(0, 0, 0, 0.45)",
                fontWeight: 50,
              }}
            >
              Esse painel contém {emails.total} emails
            </div>
          </div>
        }
        extra={
          !protectedView && (
            <Dropdown menu={menu} trigger={["click"]}>
              <Button type="text" icon={<MoreHorizontal size={16} />} />
            </Dropdown>
          )
        }
        style={{
          backgroundColor: "#2791F5",
          display: "flex",
          flexDirection: "column",
          minWidth: 350,
          maxHeight: "85vh",
        }}
        bodyStyle={{
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: 1,
          overflow: "hidden",
        }}
        {...(protectedView ? preventCopyProps(350, "#2791F5") : {})}
      >
        <Input
          allowClear
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filtrar e-mail"
          prefix={<SearchIcon size={14} />}
          autoComplete={false}
          style={{ marginBottom: 20, height: "40px" }}
        />

        <div
          ref={listContainerRef}
          style={{ flex: 1, overflow: "auto", paddingRight: 20 }}
          className="custom-scroll"
        >
          <List
            dataSource={emails.items}
            renderItem={(email) => (
              <List.Item
                style={{
                  backgroundColor: "#ffff",
                  marginBottom: 15,
                  padding: "8px 12px",
                  borderRadius: 10,
                  boxShadow:
                    "0 1px 1px rgba(0,0,0,0.08), 0 3px 2px rgba(0,0,0,0.18)",
                }}
                actions={[
                  !protectedView && (
                    <Popconfirm
                      title="Remover este e-mail?"
                      onConfirm={() => removeOne(email.id)}
                    >
                      <Trash2 size={14} style={{ cursor: "pointer" }} />
                    </Popconfirm>
                  ),
                ]}
              >
                {email.value}
              </List.Item>
            )}
            locale={{ emptyText: "Nenhum e-mail" }}
          />
          {hasMore && (
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Button onClick={handleLoadMore}>Carregar mais</Button>
            </div>
          )}
        </div>
      </Card>

      <Modal
        open={bulkOpen}
        title="Colar lista de e-mails"
        onCancel={() => setBulkOpen(false)}
        onOk={handleBulkSave}
        okText="Salvar"
        confirmLoading={loading}
      >
        <Text type="secondary">
          Cole uma lista de e-mails (separados por espaço, vírgula, ponto e
          vírgula ou quebra de linha)
        </Text>
        <Input.TextArea
          rows={6}
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          style={{ marginTop: 8 }}
          placeholder="email@exemple.com; email2@exemple.com; email3@exemple.com ..."
        />
        <Divider />
        <Radio.Group
          value={bulkMode}
          onChange={(e) => setBulkMode(e.target.value)}
        >
          <Radio value="NEW">Adicionar aos existentes</Radio>
          <Radio value="REPLACE">Substituir lista atual</Radio>
        </Radio.Group>
      </Modal>
    </Spin>
  );
}
